import { CircularProgress } from '@mui/material'
import React, { ChangeEventHandler, FC, useContext, useState } from 'react'

import { DamageNumberField } from 'common/components/Devk'
import { CheckboxField, InputFieldProps } from 'common/components/Form'
import { DEDUCTIBLE_AMOUNTS, protectionAmounts } from 'common/constants'
import { UserContext } from 'common/user-context'
import * as roles from 'common/utils/roles'
import { checkDamageNumber } from 'packages/chancellery-search/actions'
import { CheckDamageNumberQuery } from 'packages/chancellery-search/interfaces/schemaDefinition'

export type DamageNumberData = Partial<{
  vnr: string
  disputeAmount: string
  deductible: boolean
  deductibleAmount: string
  noProtection: boolean | null
  protectionAmount: Array<string>
  firstname: string
  lastname: string
  foa: 'Herr' | 'Frau'
  maritalStatus: string
  street: string
  streetNr: string
  city: string
  zip: string
  email: string
  phone: string
}>

type DamageNumberinputProps = {
  damageNumberDisabled?: boolean
  damageNumber: string
  error?: string
  maskOptions?: InputFieldProps['maskOptions']
  onFieldChange: React.EventHandler<React.FormEvent<HTMLInputElement>>
  onCheckboxChange: React.EventHandler<React.FormEvent<HTMLInputElement>>
  onCaseDataChange: (data: DamageNumberData) => void
}

type DamageNumberResponse = CheckDamageNumberQuery['checkDamageNumber']
type ProtectionAmountProperties = keyof Omit<typeof protectionAmounts, 'unknown'>
type ProtectionAmountOptions = Pick<DamageNumberResponse, ProtectionAmountProperties>

const getProtectionAmount = (amount: ProtectionAmountOptions): Array<string> =>
  [
    amount.consultation ? protectionAmounts.consultation : '',
    amount.extrajudicial ? protectionAmounts.extrajudicial : '',
    amount.firstInstance ? protectionAmounts.firstInstance : '',
  ].filter(Boolean)

export const DamageNumberInput: FC<DamageNumberinputProps> = ({
  onFieldChange,
  onCheckboxChange,
  onCaseDataChange,
  error,
  damageNumber,
  damageNumberDisabled,
  maskOptions,
}) => {
  const [loading, setLoading] = useState(false)
  const [badFetch, setBadFetch] = useState('')

  const { user } = useContext(UserContext)
  const hasWebserviceAccess = roles.isChannel(user)

  const onTakeData = (data: DamageNumberResponse): void => {
    const { firstname, lastname, street, streetNr, city, zip, phone, email, gender } = data
    const deductibleAmount = data.deductibleAmount?.replace('.', ',')
    const protectionAmount = getProtectionAmount(data)

    /**
     * every protection === false => noProtection = true
     * some protection === true => noProtection = false
     * no explicit protection === true => noProtection = null
     */
    const protections = Object.keys(protectionAmounts).filter(key => key !== 'unknown')
    const hasNoProtection = protections.every((a: ProtectionAmountProperties) => data[a] === false)
    const hasProtection = protections.some((a: ProtectionAmountProperties) => data[a] === true)
    const noProtection = hasNoProtection || (hasProtection ? false : null)

    const deductible = !!parseInt(data.deductibleAmount ?? '0', 10)
    onCaseDataChange({
      vnr: data.vnr ?? '',
      disputeAmount: data.disputeAmount?.replace('.', ',') ?? '',
      /**
       * Having a deductible without protection
       * is nonsensical.
       */
      deductible: !noProtection && deductible,
      deductibleAmount:
        !noProtection && deductibleAmount && DEDUCTIBLE_AMOUNTS.includes(deductibleAmount) ? deductibleAmount : '',
      noProtection,
      protectionAmount,
      firstname: firstname ?? '',
      lastname: lastname ?? '',
      street: street ?? '',
      streetNr: streetNr ?? '',
      city: city ?? '',
      zip: zip ?? '',
      phone: phone ?? '',
      email: email ?? '',
      foa: gender === '2' ? 'Frau' : gender === '1' ? 'Herr' : undefined,
    })
  }

  const onDamageNumberChange: ChangeEventHandler<HTMLInputElement> = async event => {
    onFieldChange(event)
    const cleanedDamageNumber = event.target.value.replace(/\D/g, '')
    const getDamageData = hasWebserviceAccess && cleanedDamageNumber.length === 14

    if (!getDamageData) {
      return
    }

    setBadFetch('')
    setLoading(true)

    try {
      const response = await checkDamageNumber(cleanedDamageNumber)

      if (!response.exists) {
        setBadFetch('Es existieren keine Daten zu dieser Schadennummer')
      } else {
        onTakeData(response)
      }
    } catch (_e) {
      setBadFetch('Es konnten keine Daten abgerufen werden')
    }

    setLoading(false)
  }

  return (
    <>
      <DamageNumberField
        label="Schadensnummer"
        disabled={damageNumberDisabled}
        value={damageNumber}
        maskOptions={maskOptions}
        onChange={onDamageNumberChange}
        name="damageNumber"
        id="input-damageNumber"
        error={badFetch || error}
        width="24rem"
      />
      {loading && <CircularProgress style={{ marginTop: '2rem', marginLeft: '-3rem', marginRight: '1.5rem' }} size={24} />}
      <CheckboxField
        className="padding-top-1-5rem"
        onChange={onCheckboxChange}
        checked={!!damageNumberDisabled}
        name="damageNumberDisabled"
        id="checkbox-damageNumberDisabled"
        label="Keine Schadensnummer vorhanden"
      />
    </>
  )
}
